import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createHead } from '@unhead/vue/client'

import App from '@/App.vue'
import router from '@/global/router'
import i18n, { initializeI18n } from '@/global/i18n'
import { useAuthStore } from '@/stores/auth'

// import global SCSS styles
import '@/assets/styles/main.scss'
import '@claitontech/chap-lib/dist/chap-lib.css'

import * as Sentry from '@sentry/vue'
import apiService from '@/api/index' // Import the apiService
import Clarity from '@microsoft/clarity'
import { runtimeConfig } from '@/config/runtime'

// Initialize i18n with the correct language before creating the app
async function initApp() {
  await initializeI18n()

  const app = createApp(App)
  const pinia = createPinia()

  // Distributed tracing targets come from the runtime config as a comma-separated
  // string (e.g. "localhost,https://api.test.skadesvar.dev"); fall back to the
  // local defaults when unset.
  const tracePropagationTargets = runtimeConfig.sentryTracesPropagationTargets
    ? runtimeConfig.sentryTracesPropagationTargets
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : ['localhost', /^https:\/\/api\.test\.skadesvar\.dev/]

  Sentry.init({
    app,
    dsn:
      runtimeConfig.sentryDsn ||
      'https://3085448df40ca8f717d486050d1e2a45@o4507526836977664.ingest.de.sentry.io/4510073883721808',
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    environment: runtimeConfig.appEnv,
    // Tracing
    tracesSampleRate: runtimeConfig.sentryTracesSampleRate ?? 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets,
    // Session Replay
    replaysSessionSampleRate: runtimeConfig.sentryReplaysSessionSampleRate ?? 0.0, // Turning off the sampling of all sessions
    replaysOnErrorSampleRate: runtimeConfig.sentryReplaysOnErrorSampleRate ?? 0.0, // Turning off the sampling of sessions with errors
  })
  Clarity.init(runtimeConfig.clarityProjectId || 'tfpef8bruz')

  // Provide the apiService globally
  app.provide('apiService', apiService)

  const head = createHead()
  app.use(head)

  app.use(pinia)
  app.use(i18n)
  app.use(router)

  const authStore = useAuthStore(pinia)
  authStore.registerAuthListeners()

  app.mount('#app')
}

// Initialize the application
initApp().catch(console.error)
